import { Camera, CameraSource, CameraResultType } from '@capacitor/camera';
import { isInMobile } from '@fabric-app/compat';
import { detectMobile } from '@fabric-app/utils/detection';
import { captureException } from '@fabric-app/compat/capabilities/sentry';
import dayjs from 'dayjs';
import { fileTypeFromBuffer } from 'file-type';
import { pickImages } from './pickImages.js';
import { CameraError } from '../mediaRecording.types.js';
import { toast } from '../../store/alerts.js';
import { isError, ensureError } from '@fabric-app/utils/guards';
import { base64ToUint8Array } from '@fabric-app/utils/files';
import { requestCameraPermission } from '@fabric-app/utils/dom-permissions';
import { getCompatFile } from '@fabric-app/compat/capabilities/file';

const takePicture = async () => {
  try {
    if (!detectMobile()) {
      const images = await pickImages({
        limit: 1
      });
      if (images.length === 0) {
        throw new Error(CameraError.Cancel);
      }
      return images[0];
    }
    if (!isInMobile()) {
      const result = await requestCameraPermission();
      switch (result) {
        case "denied":
          throw new Error(CameraError.PermissionCameraDenied);
        case "notfound":
          throw new Error(CameraError.CameraUnavailable);
        case "unknown":
          throw new Error(CameraError.PermissionCameraUnavailable);
      }
    }
    const image = await Camera.getPhoto({
      quality: 90,
      saveToGallery: false,
      resultType: CameraResultType.Uri,
      source: CameraSource.Camera
    });
    const fileCompat = getCompatFile();
    if (!fileCompat.getIsAvailable()) throw new Error("File service not found");
    const path = image.path ?? image.webPath;
    if (!path) throw new Error("No image path");
    const { content } = await fileCompat.readFile({
      url: path
    });
    let name = `Photo ${dayjs().format("YYYY-MM-DD HH:MM")}`;
    let contentType = "image/jpeg";
    const binary = base64ToUint8Array(content, 4100);
    const parsedFileType = await fileTypeFromBuffer(binary);
    if (parsedFileType) {
      name += `.${parsedFileType.ext}`;
      contentType = parsedFileType.mime;
    }
    const byteArray = Buffer.from(content, "base64");
    const blob = new Blob([
      byteArray
    ], {
      type: contentType
    });
    const file = new File([
      blob
    ], name, {
      type: contentType
    });
    return file;
  } catch (error) {
    if (isError(error) && (error.name === "NotAllowedError" || error.name === "PermissionDeniedError" || error.name === "SecurityError" || error.message === CameraError.PermissionCameraDenied || error.message === CameraError.PermissionPhotosDenied)) {
      toast({
        id: "camera-permission-denied",
        content: "You need to grant camera access!",
        replace: true
      });
      throw ensureError(error);
    }
    if (isError(error) && (error.message === CameraError.PermissionCameraUnavailable || error.message === CameraError.CameraUnavailable)) {
      toast({
        id: "camera-unavailable",
        content: "Unable to access your device's camera",
        replace: true
      });
      throw ensureError(error);
    }
    if (!isError(error) || error.message !== CameraError.Cancel) {
      captureException(ensureError(error));
    }
    throw ensureError(error);
  }
};

export { takePicture };
//# sourceMappingURL=takePicture.js.map
